import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, Phone, Target, Activity, TrendingUp, CheckCircle2,
  Route as RouteIcon, Award, Layers, Factory, Users2,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/common/Card';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/components/common/Toast';
import * as dashboardService from '@/services/api/dashboardService';
import type { DashboardStats, DashboardAnalytics } from '@/services/api/dashboardService';

const PALETTE = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#14b8a6'];
const PAIN_COLORS: Record<string, string> = { VISIBILITY: '#3b82f6', EXECUTION: '#f97316', DATA: '#8b5cf6', QUALITY: '#ef4444', INTEGRATION: '#14b8a6' };
const READINESS_COLORS: Record<string, string> = { NOT_READY: '#94a3b8', WARMING: '#f59e0b', NEAR_READY: '#3b82f6', READY: '#10b981' };

const RANGES = [
  { label: '7D', days: 7 }, { label: '30D', days: 30 }, { label: '90D', days: 90 }, { label: '1Y', days: 365 },
];

function isoDaysAgo(days: number) {
  return new Date(Date.now() - days * 864e5).toISOString().slice(0, 10);
}

// Rich tooltip: shows every series + total + percent share
function RichTooltip({ active, payload, label, total }: any) {
  if (!active || !payload?.length) return null;
  const sum = payload.reduce((s: number, p: any) => s + (p.value || 0), 0);
  return (
    <div className="rounded-lg border border-border bg-background/95 backdrop-blur px-3 py-2 shadow-lg text-xs">
      {label != null && <p className="font-semibold text-foreground mb-1">{label}</p>}
      {payload.map((p: any, i: number) => (
        <div key={i} className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.fill }} />
            {p.name}
          </span>
          <span className="font-mono font-semibold">
            {p.value}{total ? <span className="text-muted-foreground ml-1">({((p.value / (total || 1)) * 100).toFixed(0)}%)</span> : null}
          </span>
        </div>
      ))}
      {payload.length > 1 && <div className="mt-1 pt-1 border-t border-border flex justify-between gap-4"><span>Total</span><span className="font-mono font-semibold">{sum}</span></div>}
    </div>
  );
}

function ChartCard({ title, icon: Icon, children, span }: { title: string; icon: any; children: React.ReactNode; span?: string }) {
  return (
    <Card className={span}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          <Icon className="h-4 w-4 text-primary" />{title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  );
}

function KpiCard({ label, value, sub, icon: Icon, color, onClick }: any) {
  return (
    <button onClick={onClick}
      className="text-left p-3 rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </button>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [rangeDays, setRangeDays] = useState(90);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [s, a] = await Promise.all([
          dashboardService.getDashboardStats(),
          dashboardService.getDashboardAnalytics(isoDaysAgo(rangeDays), new Date().toISOString().slice(0, 10)),
        ]);
        setStats(s); setAnalytics(a);
      } catch (e: any) {
        addToast({ type: 'error', title: 'Error', description: e.response?.data?.error || 'Failed to load dashboard' });
      } finally { setLoading(false); }
    })();
  }, [rangeDays]);

  const painRadar = useMemo(() => (analytics?.byPainAxis || []).map(p => ({ axis: p.name, value: p.value })), [analytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-primary mb-3" />
          <p className="text-muted-foreground text-sm">Loading dashboard…</p>
        </div>
      </div>
    );
  }
  if (!stats || !analytics) return <div className="text-center py-12 text-muted-foreground">Failed to load dashboard.</div>;

  const t = analytics.totals;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PageHeader title="Dashboard" description="ABM performance at a glance" icon={Activity} />
        <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
          {RANGES.map(r => (
            <button key={r.label} onClick={() => setRangeDays(r.days)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${rangeDays === r.days ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <KpiCard label="Accounts" value={stats.accounts.total} sub={`${stats.accounts.active} active`} icon={Building2} color="#6366f1" onClick={() => navigate('/accounts')} />
        <KpiCard label="Contacts" value={stats.contacts.total} sub={`${stats.contacts.decisionMakers} DMs`} icon={Users} color="#10b981" onClick={() => navigate('/contacts')} />
        <KpiCard label="MQL Submitted" value={t.mqlSubmitted} sub={`${t.mqlAccepted} accepted`} icon={TrendingUp} color="#f59e0b" onClick={() => navigate('/mql')} />
        <KpiCard label="Qualified MQL" value={t.qualifiedMql} sub="BANT+MES-fit" icon={CheckCircle2} color="#8b5cf6" onClick={() => navigate('/mql-readiness')} />
        <KpiCard label="Journeys" value={t.journeysActive} sub={`${t.journeysCompleted} done`} icon={RouteIcon} color="#ec4899" onClick={() => navigate('/abm-journeys')} />
        <KpiCard label="Calls (range)" value={analytics.callTrend.reduce((s, c) => s + c.count, 0)} sub={`${stats.callLogs.thisWeek} this wk`} icon={Phone} color="#f97316" onClick={() => navigate('/calls')} />
        <KpiCard label="Tier 1" value={stats.accounts.tier1} sub="strategic" icon={Award} color="#eab308" onClick={() => navigate('/accounts')} />
      </div>

      {/* Buyer vs Partner tracks — separate data cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Buyer track */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="h-4 w-4 text-indigo-500" />
            <span className="text-sm font-semibold">Buyer Track</span>
            <span className="ml-auto text-[11px] text-muted-foreground">end customer → MQL</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => navigate('/accounts')} className="text-left rounded-lg border border-border p-2 hover:border-primary/40">
              <div className="text-xl font-bold">{stats.accounts.buyer ?? t.buyerAccounts ?? 0}</div>
              <div className="text-[10px] text-muted-foreground">Accounts</div>
            </button>
            <button onClick={() => navigate('/abm-journeys')} className="text-left rounded-lg border border-border p-2 hover:border-primary/40">
              <div className="text-xl font-bold">{analytics.journeysByType?.buyer.active ?? 0}</div>
              <div className="text-[10px] text-muted-foreground">Active journeys</div>
            </button>
            <button onClick={() => navigate('/mql-readiness')} className="text-left rounded-lg border border-border p-2 hover:border-primary/40">
              <div className="text-xl font-bold text-emerald-600">{t.qualifiedMql}</div>
              <div className="text-[10px] text-muted-foreground">Qualified MQL</div>
            </button>
          </div>
        </Card>

        {/* Partner track */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users2 className="h-4 w-4 text-rose-500" />
            <span className="text-sm font-semibold">Partner Track</span>
            <span className="ml-auto text-[11px] text-muted-foreground">channel → PQL</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => navigate('/accounts')} className="text-left rounded-lg border border-border p-2 hover:border-primary/40">
              <div className="text-xl font-bold">{stats.accounts.partner ?? t.partnerAccounts ?? 0}</div>
              <div className="text-[10px] text-muted-foreground">Partners</div>
            </button>
            <button onClick={() => navigate('/abm-journeys')} className="text-left rounded-lg border border-border p-2 hover:border-primary/40">
              <div className="text-xl font-bold">{analytics.journeysByType?.partner.active ?? 0}</div>
              <div className="text-[10px] text-muted-foreground">Active journeys</div>
            </button>
            <button onClick={() => navigate('/mql-readiness')} className="text-left rounded-lg border border-border p-2 hover:border-primary/40">
              <div className="text-xl font-bold text-emerald-600">{t.qualifiedPql ?? 0}</div>
              <div className="text-[10px] text-muted-foreground">Qualified PQL</div>
            </button>
          </div>
        </Card>
      </div>

      {/* Row 1: Industry (wide) + Region + Tier donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Accounts by Industry × Tier" icon={Factory} span="lg:col-span-2">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={analytics.industryByTier} margin={{ top: 4, right: 8, left: -18, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-12} textAnchor="end" height={48} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<RichTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Tier1" stackId="a" fill="#eab308" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Tier2" stackId="a" fill="#8b5cf6" />
              <Bar dataKey="Tier3" stackId="a" fill="#94a3b8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Account Tier Split" icon={Target}>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={analytics.byTier} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                {analytics.byTier.map((_, i) => <Cell key={i} fill={['#eab308', '#8b5cf6', '#94a3b8'][i]} />)}
              </Pie>
              <Tooltip content={<RichTooltip total={stats.accounts.total} />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 2: Region bar + Pain radar + Maturity bar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Accounts by Region" icon={Building2}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={analytics.byRegion.slice(0, 8)} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} />
              <Tooltip content={<RichTooltip total={stats.accounts.total} />} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {analytics.byRegion.slice(0, 8).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Primary Pain Axis" icon={Layers}>
          <ResponsiveContainer width="100%" height={220}>
            <RadarChart data={painRadar} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
              <PolarGrid opacity={0.3} />
              <PolarAngleAxis dataKey="axis" tick={{ fontSize: 10 }} />
              <PolarRadiusAxis tick={{ fontSize: 9 }} />
              <Radar dataKey="value" stroke="#6366f1" fill="#6366f1" fillOpacity={0.5} />
              <Tooltip content={<RichTooltip total={stats.accounts.total} />} />
            </RadarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Maturity Band Distribution" icon={TrendingUp}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={analytics.byMaturity} margin={{ top: 4, right: 8, left: -18, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<RichTooltip total={stats.accounts.total} />} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {analytics.byMaturity.map((_, i) => <Cell key={i} fill={['#ef4444', '#f97316', '#eab308', '#10b981', '#06b6d4'][i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 3: MQL trend (wide) + readiness donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="MQL Submitted vs Accepted (over time)" icon={TrendingUp} span="lg:col-span-2">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={analytics.mqlTrend} margin={{ top: 4, right: 8, left: -18, bottom: 4 }}>
              <defs>
                <linearGradient id="gSub" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#6366f1" stopOpacity={0.4} /><stop offset="95%" stopColor="#6366f1" stopOpacity={0} /></linearGradient>
                <linearGradient id="gAcc" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.4} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={24} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<RichTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="submitted" stroke="#6366f1" fill="url(#gSub)" strokeWidth={2} />
              <Area type="monotone" dataKey="accepted" stroke="#10b981" fill="url(#gAcc)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="MQL Readiness Mix" icon={CheckCircle2}>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={analytics.readinessData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={{ fontSize: 10 }}>
                {analytics.readinessData.map((d, i) => <Cell key={i} fill={READINESS_COLORS[d.name] || PALETTE[i]} />)}
              </Pie>
              <Tooltip content={<RichTooltip />} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 4: Journey funnel (wide) + plant type + status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Journey Funnel (contacts reaching each stage)" icon={RouteIcon} span="lg:col-span-2">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={analytics.funnel} margin={{ top: 4, right: 8, left: -18, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
              <XAxis dataKey="stage" tick={{ fontSize: 10 }} interval={0} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<RichTooltip />} />
              <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Plant Type" icon={Factory}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={analytics.byPlant.slice(0, 7)} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
              <Tooltip content={<RichTooltip total={stats.accounts.total} />} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {analytics.byPlant.slice(0, 7).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 5: Region × Pain stacked (wide) + call trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Region × Pain Axis" icon={Layers} span="lg:col-span-2">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={analytics.regionByPain} margin={{ top: 4, right: 8, left: -18, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<RichTooltip />} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {Object.keys(PAIN_COLORS).map(p => (
                <Bar key={p} dataKey={p} stackId="p" fill={PAIN_COLORS[p]} radius={p === 'INTEGRATION' ? [4, 4, 0, 0] : undefined} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Calls Logged (over time)" icon={Phone}>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={analytics.callTrend} margin={{ top: 4, right: 8, left: -18, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={24} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<RichTooltip />} />
              <Line type="monotone" dataKey="count" stroke="#f97316" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
